import React from 'react';
import { SEMHR_TOUR, SEMHR_CONVERSATION } from './semanticHrTourData';

import { BRAND, PARTNER_LOGOS } from '../../../../content/staticAssets';

export function PowerBILogo({ className = 'h-3.5 w-3.5' }) {
  return (
    <img src={PARTNER_LOGOS.powerbi} alt="" aria-hidden="true" className={`inline-block object-contain ${className}`} draggable={false} />
  );
}

export function MyOrgLabMark({ className = '' }) {
  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      <img src={BRAND.logoMark} alt="" aria-hidden="true" className="h-4 w-4 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      <span className="text-[7px] font-bold tracking-[0.08em] text-[#1e4d8c]">MYORGLAB</span>
    </div>
  );
}

export const PBI_CHART_SVG_CLASS = 'block h-full w-full min-h-0';
export const PBI_CHART_ASPECT = 'none';
export const PBI_CHART_FIT = 'xMidYMin meet';

function PbiChartTitle({ children, x = 0, y = 10 }) {
  return (
    <text x={x} y={y} fontSize="7" fontWeight="600" fill="#1e4d8c">{children}</text>
  );
}

function PbiLegend({ items, x, y }) {
  return (
    <g transform={`translate(${x}, ${y})`}>
      {items.map((item, i) => (
        <g key={item.label} transform={`translate(${i * 52}, 0)`}>
          {item.dash ? (
            <line x1={0} y1={3} x2={8} y2={3} stroke={item.color} strokeWidth="1.5" strokeDasharray="3 2" />
          ) : (
            <rect x={0} y={0} width={8} height={6} fill={item.color} rx="0.5" />
          )}
          <text x={10} y={5} fontSize="6" fill="#64748b">{item.label}</text>
        </g>
      ))}
    </g>
  );
}

export function HiresTermsChartSvg({ className = '' }) {
  const { chart } = SEMHR_TOUR;
  const w = 360;
  const h = 118;
  const pad = { l: 32, r: 8, t: 12, b: 28 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const max = 250;
  const groupW = innerW / chart.months.length;
  const barW = 11;
  const barGap = 2;
  const HIRES = 'rgba(10, 31, 58, 0.88)';
  const TERMS = '#89cff0';
  const axisTickSize = 6;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`block w-full ${className}`} shapeRendering="crispEdges">
      {[0, 50, 100, 150, 200, 250].map((tick) => {
        const y = pad.t + innerH - (tick / max) * innerH;
        return (
          <g key={tick}>
            <line x1={pad.l} y1={y} x2={w - pad.r} y2={y} stroke="#e2e8f0" strokeWidth="0.75" />
            <text x={pad.l - 4} y={y + 2} textAnchor="end" fontSize={axisTickSize} fill="#64748b">{tick}</text>
          </g>
        );
      })}
      <text x={14} y={pad.t + innerH / 2} fontSize={axisTickSize} fill="#64748b" transform={`rotate(-90 14 ${pad.t + innerH / 2})`}>Count</text>
      {chart.months.map((month, i) => {
        const cx = pad.l + i * groupW + groupW / 2;
        const hireH = (chart.hires[i] / max) * innerH;
        const termH = (chart.terms[i] / max) * innerH;
        const baseY = pad.t + innerH;
        return (
          <g key={month}>
            <rect x={cx - barW - barGap} y={baseY - hireH} width={barW} height={Math.max(hireH, 1)} fill={HIRES} />
            <rect x={cx + barGap} y={baseY - termH} width={barW} height={Math.max(termH, 1)} fill={TERMS} />
            <text x={cx} y={h - 16} textAnchor="middle" fontSize={axisTickSize} fill="#64748b">{month}</text>
          </g>
        );
      })}
      <text x={pad.l + innerW / 2} y={h - 4} textAnchor="middle" fontSize={axisTickSize} fill="#64748b">Month</text>
    </svg>
  );
}

export function TerminationLineChart({ className = '' }) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const voluntary = [812, 848, 862, 834, 878, 856, 842, 892, 868, 904, 918, 886];
  const involuntary = [328, 342, 356, 338, 362, 348, 334, 372, 358, 368, 382, 364];
  const w = 280;
  const h = 88;
  const pad = { l: 30, r: 8, t: 16, b: 18 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const max = 1000;
  const scaleY = (v) => pad.t + innerH - (v / max) * innerH;
  const scaleX = (i, len) => pad.l + (i / (len - 1)) * innerW;
  const toPoints = (data) => data.map((v, i) => `${scaleX(i, data.length)},${scaleY(v)}`).join(' ');
  const volArea = [
    ...voluntary.map((v, i) => `${scaleX(i, voluntary.length)},${scaleY(v)}`),
    `${scaleX(voluntary.length - 1, voluntary.length)},${pad.t + innerH}`,
    `${scaleX(0, voluntary.length)},${pad.t + innerH}`,
  ].join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`${PBI_CHART_SVG_CLASS} ${className}`} preserveAspectRatio={PBI_CHART_ASPECT} shapeRendering="geometricPrecision">
      <PbiChartTitle x={0} y={10}>Monthly Terminations by Type</PbiChartTitle>
      {[0, 400, 800].map((tick) => (
        <g key={tick}>
          <line x1={pad.l} y1={scaleY(tick)} x2={w - pad.r} y2={scaleY(tick)} stroke="#e8edf2" strokeWidth="0.75" />
          <text x={pad.l - 4} y={scaleY(tick) + 2} textAnchor="end" fontSize="5.5" fill="#94a3b8">{tick}</text>
        </g>
      ))}
      <polygon fill="#fecaca" fillOpacity="0.35" points={volArea} />
      <polyline fill="none" stroke="#ef4444" strokeWidth="1.75" points={toPoints(voluntary)} />
      <polyline fill="none" stroke="#1e4d8c" strokeWidth="1.75" points={toPoints(involuntary)} />
      {voluntary.map((v, i) => (
        <circle key={`v-${i}`} cx={scaleX(i, voluntary.length)} cy={scaleY(v)} r="1.5" fill="#fff" stroke="#ef4444" strokeWidth="1" />
      ))}
      {involuntary.map((v, i) => (
        <circle key={`i-${i}`} cx={scaleX(i, involuntary.length)} cy={scaleY(v)} r="1.5" fill="#fff" stroke="#1e4d8c" strokeWidth="1" />
      ))}
      {months.filter((_, i) => i % 2 === 0).map((m, idx) => (
        <text key={m} x={scaleX(idx * 2, months.length)} y={h - 4} textAnchor="middle" fontSize="5.5" fill="#64748b">{m}</text>
      ))}
      <PbiLegend
        x={pad.l}
        y={h - 10}
        items={[
          { label: 'Voluntary', color: '#ef4444' },
          { label: 'Involuntary', color: '#1e4d8c' },
        ]}
      />
    </svg>
  );
}

export function QuarterlyStackedBarChart({ quarters, className = '' }) {
  const w = 280;
  const h = 88;
  const titleH = 16;
  const chartTop = titleH + 2;
  const chartH = h - chartTop - 14;
  const pad = { l: 28, r: 8, b: 12 };
  const innerW = w - pad.l - pad.r;
  const max = Math.max(...quarters.map((q) => q.voluntary + q.involuntary));
  const barW = innerW / quarters.length - 4;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`${PBI_CHART_SVG_CLASS} ${className}`} preserveAspectRatio={PBI_CHART_ASPECT} shapeRendering="crispEdges">
      <PbiChartTitle x={0} y={10}>Quarterly Terminations</PbiChartTitle>
      {[0.25, 0.5, 0.75, 1].map((pct) => {
        const y = chartTop + chartH * (1 - pct);
        return <line key={pct} x1={pad.l} y1={y} x2={w - pad.r} y2={y} stroke="#eef2f6" strokeWidth="0.5" />;
      })}
      {quarters.map((q, i) => {
        const x = pad.l + i * (innerW / quarters.length) + 2;
        const volH = (q.voluntary / max) * chartH;
        const invH = (q.involuntary / max) * chartH;
        const baseY = chartTop + chartH;
        return (
          <g key={q.label}>
            <rect x={x} y={baseY - volH - invH} width={barW} height={Math.max(volH, 1)} fill="#1e4d8c" rx="0.5" />
            <rect x={x} y={baseY - invH} width={barW} height={Math.max(invH, 1)} fill="#93c5fd" rx="0.5" />
            {i % 2 === 0 ? (
              <text x={x + barW / 2} y={h - 2} textAnchor="middle" fontSize="5" fill="#64748b">{q.label}</text>
            ) : null}
          </g>
        );
      })}
      <PbiLegend
        x={pad.l}
        y={h - 10}
        items={[
          { label: 'Voluntary', color: '#1e4d8c' },
          { label: 'Involuntary', color: '#93c5fd' },
        ]}
      />
    </svg>
  );
}

export function HorizontalBarChart({ title, rows, className = '', compact = false, fit = false }) {
  const w = 280;
  const h = compact ? 88 : 100;
  const titleH = 16;
  const chartTop = titleH + 2;
  const chartH = h - chartTop - 2;
  const rowH = chartH / rows.length;
  const max = Math.max(...rows.map((r) => r.value));
  const labelW = compact ? 72 : 92;
  const barStart = labelW + 4;
  const barMaxW = w - barStart - 32;
  const blues = ['#1e4d8c', '#2563eb', '#3b82f6', '#4f8fd9', '#60a5fa', '#7eb8f7', '#93c5fd', '#a5c9fa'];
  const fmt = (v) => (v >= 1000 ? v.toLocaleString() : String(v));

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`${fit ? 'block max-h-full max-w-full' : PBI_CHART_SVG_CLASS} ${className}`} preserveAspectRatio={fit ? PBI_CHART_FIT : PBI_CHART_ASPECT} shapeRendering="crispEdges">
      <PbiChartTitle x={0} y={10}>{title}</PbiChartTitle>
      {[0.25, 0.5, 0.75, 1].map((pct) => {
        const x = barStart + barMaxW * pct;
        return <line key={pct} x1={x} y1={chartTop} x2={x} y2={h - 2} stroke="#eef2f6" strokeWidth="0.5" />;
      })}
      {rows.map((row, i) => {
        const y = chartTop + i * rowH;
        const barH = Math.max(rowH * 0.62, 3);
        const bw = (row.value / max) * barMaxW;
        const color = row.color || blues[i % blues.length];
        return (
          <g key={row.label}>
            <text x={0} y={y + rowH * 0.58} fontSize="5.5" fill="#475569">{row.label.length > 14 ? `${row.label.slice(0, 13)}…` : row.label}</text>
            <rect x={barStart} y={y + (rowH - barH) / 2} width={Math.max(bw, 2)} height={barH} fill={color} rx="0.5" />
            {row.highlight ? (
              <text x={barStart + bw + 3} y={y + rowH * 0.58} fontSize="5.5" fontWeight="700" fill="#1e4d8c">{fmt(row.value)}</text>
            ) : (
              <text x={barStart + bw + 3} y={y + rowH * 0.58} fontSize="5.5" fontWeight="600" fill="#334155">{fmt(row.value)}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

export function GroupedYoYBarChart({ groups, className = '' }) {
  const w = 280;
  const h = 88;
  const titleH = 16;
  const chartTop = titleH + 2;
  const chartH = h - chartTop - 12;
  const max = Math.max(...groups.flatMap((g) => [g.y2024, g.y2025]));
  const shown = groups.slice(0, 6);
  const rowH = chartH / shown.length;
  const barStart = 88;
  const barMaxW = w - barStart - 12;
  const barGap = 2;
  const barH = Math.max(rowH * 0.34, 3);

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`${PBI_CHART_SVG_CLASS} ${className}`} preserveAspectRatio={PBI_CHART_ASPECT} shapeRendering="crispEdges">
      <PbiChartTitle x={0} y={10}>Hires by Business Unit (YoY)</PbiChartTitle>
      {[0.25, 0.5, 0.75, 1].map((pct) => {
        const x = barStart + barMaxW * pct;
        return <line key={pct} x1={x} y1={chartTop} x2={x} y2={h - 12} stroke="#eef2f6" strokeWidth="0.5" />;
      })}
      {shown.map((g, i) => {
        const y = chartTop + i * rowH;
        const b2024 = (g.y2024 / max) * barMaxW;
        const b2025 = (g.y2025 / max) * barMaxW;
        return (
          <g key={g.label}>
            <text x={0} y={y + rowH * 0.42} fontSize="6" fill="#475569">{g.label.length > 14 ? `${g.label.slice(0, 13)}…` : g.label}</text>
            <rect x={barStart} y={y + barGap} width={Math.max(b2024, 2)} height={barH} fill="#1e4d8c" rx="0.5" />
            <rect x={barStart} y={y + barGap + barH + 1} width={Math.max(b2025, 2)} height={barH} fill="#19e3f2" rx="0.5" />
          </g>
        );
      })}
      <PbiLegend
        x={barStart}
        y={h - 8}
        items={[
          { label: '2024', color: '#1e4d8c' },
          { label: '2025', color: '#19e3f2' },
        ]}
      />
    </svg>
  );
}

export function HiresVsPlanLineChart({ planLine, className = '' }) {
  const { months, actual, planned } = planLine;
  const w = 280;
  const h = 88;
  const pad = { l: 34, r: 8, t: 18, b: 22 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const min = 880;
  const max = 1020;
  const scaleY = (v) => pad.t + innerH - ((v - min) / (max - min)) * innerH;
  const scaleX = (i, len) => pad.l + (i / (len - 1)) * innerW;
  const toPoints = (data) => data.map((v, i) => `${scaleX(i, data.length)},${scaleY(v)}`).join(' ');
  const areaPoints = [
    ...actual.map((v, i) => `${scaleX(i, actual.length)},${scaleY(v)}`),
    `${scaleX(actual.length - 1, actual.length)},${pad.t + innerH}`,
    `${scaleX(0, actual.length)},${pad.t + innerH}`,
  ].join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`${PBI_CHART_SVG_CLASS} ${className}`} preserveAspectRatio={PBI_CHART_ASPECT} shapeRendering="geometricPrecision">
      <PbiChartTitle x={0} y={10}>Monthly Hires vs Plan</PbiChartTitle>
      {[900, 950, 1000].map((tick) => (
        <g key={tick}>
          <line x1={pad.l} y1={scaleY(tick)} x2={w - pad.r} y2={scaleY(tick)} stroke="#e8edf2" strokeWidth="0.75" />
          <text x={pad.l - 4} y={scaleY(tick) + 2} textAnchor="end" fontSize="6" fill="#94a3b8">{tick}</text>
        </g>
      ))}
      <polygon fill="#14b8a6" fillOpacity="0.12" points={areaPoints} />
      <polyline fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeDasharray="5 3" points={toPoints(planned)} />
      <polyline fill="none" stroke="#14b8a6" strokeWidth="2.25" points={toPoints(actual)} />
      {actual.map((v, i) => {
        const x = scaleX(i, actual.length);
        return <circle key={i} cx={x} cy={scaleY(v)} r="2.25" fill="#fff" stroke="#14b8a6" strokeWidth="1.5" />;
      })}
      {months.filter((_, i) => i % 2 === 0).map((m, idx) => (
        <text key={m} x={scaleX(idx * 2, months.length)} y={h - 6} textAnchor="middle" fontSize="6" fill="#64748b">{m}</text>
      ))}
      <PbiLegend
        x={pad.l}
        y={h - 12}
        items={[
          { label: 'Actual', color: '#14b8a6' },
          { label: 'Plan', color: '#94a3b8', dash: true },
        ]}
      />
    </svg>
  );
}

export function HeadcountPlanLineChart({ planLine, className = '', fit = false }) {
  const months = planLine?.months || ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
  const active = planLine?.active || [19420, 19510, 19620, 19710, 19830, 19983];
  const planned = planLine?.planned || [19800, 19900, 20000, 20100, 20200, 20300];
  const w = 280;
  const h = 88;
  const pad = { l: 34, r: 8, t: 16, b: 18 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const min = Math.min(...active, ...planned) - 200;
  const max = Math.max(...active, ...planned) + 200;
  const scaleY = (v) => pad.t + innerH - ((v - min) / (max - min)) * innerH;
  const scaleX = (i, len) => pad.l + (i / (len - 1)) * innerW;
  const toPoints = (data) => data.map((v, i) => `${scaleX(i, data.length)},${scaleY(v)}`).join(' ');
  const areaPoints = [
    ...active.map((v, i) => `${scaleX(i, active.length)},${scaleY(v)}`),
    `${scaleX(active.length - 1, active.length)},${pad.t + innerH}`,
    `${scaleX(0, active.length)},${pad.t + innerH}`,
  ].join(' ');

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`${fit ? 'block max-h-full max-w-full' : PBI_CHART_SVG_CLASS} ${className}`} preserveAspectRatio={fit ? PBI_CHART_FIT : PBI_CHART_ASPECT} shapeRendering="geometricPrecision">
      <PbiChartTitle x={0} y={10}>Active Headcount and Planned Headcount by Year and Month</PbiChartTitle>
      {[0.25, 0.5, 0.75].map((pct) => {
        const tick = min + (max - min) * pct;
        return (
          <g key={pct}>
            <line x1={pad.l} y1={scaleY(tick)} x2={w - pad.r} y2={scaleY(tick)} stroke="#e8edf2" strokeWidth="0.75" />
            <text x={pad.l - 4} y={scaleY(tick) + 2} textAnchor="end" fontSize="5" fill="#94a3b8">{(tick / 1000).toFixed(1)}k</text>
          </g>
        );
      })}
      <polygon fill="#93c5fd" fillOpacity="0.25" points={areaPoints} />
      <polyline fill="none" stroke="#94a3b8" strokeWidth="1.25" strokeDasharray="4 2" points={toPoints(planned)} />
      <polyline fill="none" stroke="#2563eb" strokeWidth="1.75" points={toPoints(active)} />
      {active.map((v, i) => {
        const x = scaleX(i, active.length);
        return <circle key={i} cx={x} cy={scaleY(v)} r="1.5" fill="#fff" stroke="#2563eb" strokeWidth="1" />;
      })}
      {months.filter((_, i) => i % 2 === 0).map((m, idx) => (
        <text key={m} x={scaleX(idx * 2, months.length)} y={h - 4} textAnchor="middle" fontSize="5" fill="#64748b">{m}</text>
      ))}
      <PbiLegend
        x={pad.l}
        y={h - 10}
        items={[
          { label: 'Active Headcount', color: '#2563eb' },
          { label: 'Planned Headcount', color: '#94a3b8', dash: true },
        ]}
      />
    </svg>
  );
}

const HEATMAP_ROW_SHORT = {
  'Data & AI Platforms': 'Data & AI',
  'Quality Management': 'Quality Mgmt',
  'Customer Success': 'Customer Success',
  'Hardware Engineering': 'HW Engineering',
  'Robotics & Automation': 'Robotics',
  'Finance & Accounting': 'Finance',
};

function heatColor(intensity) {
  const t = Math.min(1, Math.max(0, intensity));
  if (t >= 0.92) return '#081830';
  /* Raised floor: fewer near-white cells; middle band stays moderate */
  if (t < 0.20) return '#b0d2eb';
  if (t < 0.28) return '#a3c9e8';
  if (t < 0.38) return '#95c0e4';
  if (t < 0.48) return '#88b7df';
  if (t < 0.58) return '#7cadda';
  if (t < 0.68) return '#6ea2d4';
  if (t < 0.78) return '#5b94c8';
  if (t < 0.88) return '#457fad';
  return '#2f6696';
}

function HeatmapLegend({ className = '' }) {
  return (
    <div className={`flex items-center gap-1 text-[7px] text-ink-500 ${className}`}>
      <span>Low</span>
      <div className="h-1.5 min-w-[40px] flex-1 rounded-full bg-gradient-to-r from-[#b0d2eb] via-[#7cadda] to-[#081830]" />
      <span>High</span>
    </div>
  );
}

export function HeatmapSvg({ className = '', showLegend = false }) {
  const { heatmapRows, heatmapMonths, heatmapValues, heatmapOutlier } = SEMHR_TOUR;
  const cell = 10;
  const pad = { l: 58, r: 10, t: 10, b: 3 };
  const axisTitleX = 7;
  const gridX = pad.l;
  const gridTop = pad.t;
  const gridW = heatmapMonths.length * cell;
  const gridH = heatmapRows.length * cell;
  const w = gridX + gridW + pad.r;
  const h = gridTop + gridH + pad.b;
  const axisMidY = gridTop + gridH / 2;

  return (
    <div className={`ia-semhr-dv-heatmap-block ${className}`.trim()}>
      {showLegend ? <HeatmapLegend className="mb-px" /> : null}
      <div className="ia-semhr-dv-viz-slot ia-semhr-dv-viz-slot--heatmap">
        <svg viewBox={`0 0 ${w} ${h}`} className="ia-semhr-heatmap-svg" shapeRendering="crispEdges" preserveAspectRatio="xMidYMid meet">
          <text x={axisTitleX} y={axisMidY} fontSize="3.1" fill="#64748b" textAnchor="middle" transform={`rotate(-90 ${axisTitleX} ${axisMidY})`}>Business Unit</text>
          {heatmapMonths.map((m, i) => (
            <text key={m} x={gridX + i * cell + cell / 2} y={7} textAnchor="middle" fontSize="3.1" fill="#64748b">{m.replace("'25", '')}</text>
          ))}
          {heatmapRows.map((row, ri) => (
            <g key={row}>
              <text x={gridX - 2} y={gridTop + ri * cell + cell / 2 + 1.25} textAnchor="end" fontSize="2.85" fill="#475569">
                {HEATMAP_ROW_SHORT[row] ?? row}
              </text>
              {heatmapMonths.map((m, mi) => {
                const isOutlier = heatmapOutlier?.row === ri && heatmapOutlier?.col === mi;
                const intensity = heatmapValues?.[ri]?.[mi] ?? 0.35;
                return (
                  <rect
                    key={`${row}-${m}`}
                    x={gridX + mi * cell}
                    y={gridTop + ri * cell}
                    width={cell - 0.75}
                    height={cell - 0.75}
                    fill={isOutlier || intensity >= 0.92 ? '#081830' : heatColor(intensity)}
                    stroke="#fff"
                    strokeWidth="0.4"
                  />
                );
              })}
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

export function AttritionLineChartSvg({ className = '' }) {
  const { lineChartPoints } = SEMHR_TOUR;
  const w = 252;
  const h = 64;
  const pad = { l: 26, r: 1, t: 2, b: 5 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const min = 80;
  const max = 170;
  const lineBlue = '#1e4d8c';
  const tickSize = 3.25;
  const axisY = pad.t + innerH;
  const months = ["Jan'25", "Feb'25", "Mar'25", "Apr'25", "May'25", "Jun'25", "Jul'25", "Aug'25", "Sep'25", "Oct'25", "Nov'25", "Dec'25"];
  const points = lineChartPoints
    .map((v, i) => `${pad.l + (i / (lineChartPoints.length - 1)) * innerW},${pad.t + innerH - ((v - min) / (max - min)) * innerH}`)
    .join(' ');

  return (
    <div className={`ia-semhr-dv-viz-slot ia-semhr-dv-viz-slot--line ${className}`.trim()}>
      <svg viewBox={`0 0 ${w} ${h}`} className="ia-semhr-dv-line-chart" shapeRendering="geometricPrecision" preserveAspectRatio="xMidYMid slice">
      {[100, 120, 140, 160].map((tick) => {
        const y = pad.t + innerH - ((tick - min) / (max - min)) * innerH;
        return (
          <g key={tick}>
            <line x1={pad.l} y1={y} x2={w - pad.r} y2={y} stroke="#eef2f6" strokeWidth="0.75" />
            <text x={pad.l - 3} y={y + 1.25} textAnchor="end" fontSize={tickSize} fill="#94a3b8">{tick}</text>
          </g>
        );
      })}
      <line x1={pad.l} y1={axisY} x2={w - pad.r} y2={axisY} stroke="#e2e8f0" strokeWidth="0.75" />
      <text x={6} y={pad.t + innerH / 2} fontSize={tickSize} fill="#64748b" textAnchor="middle" transform={`rotate(-90 6 ${pad.t + innerH / 2})`}>Voluntary Terminations</text>
      <polyline fill="none" stroke={lineBlue} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" points={points} />
      {lineChartPoints.map((v, i) => {
        const x = pad.l + (i / (lineChartPoints.length - 1)) * innerW;
        const y = pad.t + innerH - ((v - min) / (max - min)) * innerH;
        return <circle key={i} cx={x} cy={y} r="1.3" fill={lineBlue} />;
      })}
      {months.filter((_, i) => i % 2 === 0).map((m, idx) => (
        <text key={m} x={pad.l + (idx * 2 / (months.length - 1)) * innerW} y={axisY + 3.5} textAnchor="middle" fontSize={tickSize} fill="#94a3b8">{m.replace("'25", '')}</text>
      ))}
      </svg>
    </div>
  );
}

/** @deprecated use AttritionLineChartSvg */
export function LineChartSvg(props) {
  return <AttritionLineChartSvg {...props} />;
}

export function HeadcountBuHorizChartSvg({ className = '', compact = false }) {
  const { chartBars: bars } = SEMHR_CONVERSATION;
  const w = 320;
  const rowH = compact ? 10 : 14;
  const barH = compact ? 6.5 : 9;
  const labelSize = compact ? 4.25 : 5;
  const axisSize = compact ? 4.25 : 5;
  const pad = compact ? { l: 68, r: 24, t: 6, b: 12 } : { l: 72, r: 28, t: 8, b: 16 };
  const h = pad.t + bars.length * rowH + pad.b;
  const innerW = w - pad.l - pad.r;
  const max = 4500;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className={`block w-full ${className}`} shapeRendering="geometricPrecision">
      <text x={pad.l} y={compact ? 5 : 6} fontSize={compact ? 5 : 6} fontWeight="600" fill="#1e4d8c">Business Unit</text>
      <text x={w - pad.r} y={compact ? 5 : 6} textAnchor="end" fontSize={compact ? 4.5 : 5.5} fill="#64748b">Headcount</text>
      {[0, 1500, 3000, 4500].map((tick) => {
        const x = pad.l + (tick / max) * innerW;
        return (
          <g key={tick}>
            <line x1={x} y1={pad.t} x2={x} y2={h - pad.b} stroke="#e2e8f0" strokeWidth="0.5" />
            <text x={x} y={h - 3} textAnchor="middle" fontSize={axisSize} fill="#94a3b8">{tick.toLocaleString()}</text>
          </g>
        );
      })}
      {bars.map((bar, i) => {
        const y = pad.t + i * rowH + (compact ? 1 : 2);
        const barW = (bar.value / max) * innerW;
        return (
          <g key={bar.label}>
            <text x={pad.l - 3} y={y + barH - 1} textAnchor="end" fontSize={labelSize} fill="#475569">{bar.label}</text>
            <rect x={pad.l} y={y} width={barW} height={barH} fill="#7eb8f7" rx="0.5" />
          </g>
        );
      })}
    </svg>
  );
}
